import { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useNation } from '@/hooks/useNation'
import { useWorld } from '@/hooks/useWorld'
import { useElection, useNationalSummary, useAllElectionResults } from '@/hooks/useElectionResults'
import { useElectionComparison } from '@/hooks/useElectionComparison'
import { useGovernmentByElection } from '@/hooks/useGovernmentByElection'
import { AppLayout } from '@/components/layout/AppLayout'
import { WinnerBanner } from '@/components/election/WinnerBanner'
import { PostElectionSummary } from '@/components/election/PostElectionSummary'
import { SeatDistribution } from '@/components/election/SeatDistribution'
import { VoteShareChart } from '@/components/election/VoteShareChart'
import { RegionalBreakdown } from '@/components/election/RegionalBreakdown'
import { ElectionComparison } from '@/components/election/ElectionComparison'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'
import type { BreadcrumbItem } from '@/components/layout/Breadcrumb'
import type { ElectionResults } from '@/types'

export default function ElectionResultsPage() {
  const { nationId, electionId } = useParams<{ nationId: string; electionId: string }>()

  const { nation, isLoading: nationLoading } = useNation(nationId)
  const { world } = useWorld(nation?.world_id)
  const { election, isLoading: electionLoading, error: electionError } = useElection(electionId)
  const { summary, isLoading: summaryLoading } = useNationalSummary(electionId)
  const { results } = useAllElectionResults(electionId)
  const {
    comparison,
    hasPreviousElection,
    previousElectionId,
    isLoading: comparisonLoading,
  } = useElectionComparison(nationId, electionId)
  const { results: previousResults } = useAllElectionResults(previousElectionId)
  const { government, isLoading: governmentLoading } = useGovernmentByElection(electionId)

  const isLoading = nationLoading || electionLoading || summaryLoading
  const electionResults = election?.results as ElectionResults | null

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Worlds', href: '/worlds' }]
    if (world) {
      items.push({ label: world.name, href: `/worlds/${world.id}` })
    }
    if (nation) {
      items.push({ label: nation.name, href: `/nations/${nation.id}/overview` })
    }
    items.push({ label: 'Election Results' })
    return items
  }, [world, nation])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      {electionError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          Failed to load election results. Please try again later.
        </div>
      )}

      {isLoading && (
        <div className="space-y-6">
          <div className="bg-muted h-8 w-64 animate-pulse rounded" />
          <div className="bg-muted h-20 animate-pulse rounded-lg" />
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="bg-muted h-5 w-40 animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="bg-muted h-64 animate-pulse rounded" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="bg-muted h-5 w-32 animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="bg-muted h-64 animate-pulse rounded" />
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {!isLoading && !electionError && election && (
        <>
          {/* Header */}
          <div className="mb-6">
            <Button variant="ghost" size="sm" asChild className="mb-4">
              <Link to={`/nations/${nationId}/overview`}>
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to {nation?.name ?? 'Nation'}
              </Link>
            </Button>
            <h1 className="text-2xl font-bold">
              {election.election_type === 'legislative' ? 'Legislative' : 'Executive'} Election Results
            </h1>
            <p className="text-muted-foreground text-sm">
              Tick {election.scheduled_tick}
              {election.is_early && ' (Early election)'}
            </p>
          </div>

          {/* Winner Banner */}
          {electionResults && (
            <WinnerBanner results={electionResults} className="mb-6" />
          )}

          {/* Post-Election Summary */}
          {electionResults && summary && results && results.length > 0 && (
            <PostElectionSummary
              electionResults={electionResults}
              summary={summary}
              results={results}
              previousResults={previousResults}
              comparison={comparison}
              hasPreviousElection={hasPreviousElection}
              government={government}
              governmentLoading={governmentLoading}
              thresholdPercent={nation?.threshold_percent ?? 5}
              nationName={nation?.name ?? 'this nation'}
              className="mb-6"
            />
          )}

          {/* Charts */}
          {summary && (
            <div className="mb-6 grid gap-6 md:grid-cols-2">
              <SeatDistribution
                partyResults={summary.party_results}
                totalSeats={summary.total_seats}
              />
              <VoteShareChart
                partyResults={summary.party_results}
                thresholdPercent={nation?.threshold_percent ?? 5}
                totalVotes={summary.total_votes}
              />
            </div>
          )}

          {/* Regional Breakdown */}
          {results && results.length > 0 && (
            <RegionalBreakdown results={results} className="mb-6" />
          )}

          {/* Historical Comparison */}
          <ElectionComparison
            comparison={comparison}
            hasPreviousElection={hasPreviousElection}
            isLoading={comparisonLoading}
          />
        </>
      )}
    </AppLayout>
  )
}
